import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useContent, useContentRefresh, useContentLoaded } from "@/context/ContentContext";

const clone = (o) => JSON.parse(JSON.stringify(o));
const pathGet = (o, path) => path.reduce((a, k) => (a == null ? a : a[k]), o);

const L = ({ children }) => (
  <label className="overline text-[color:var(--muted)] block mb-2">{children}</label>
);

function Section({ title, children }) {
  return (
    <div className="border border-black/10 p-6 sm:p-8">
      <h3 className="font-display text-2xl mb-6">{title}</h3>
      <div className="space-y-6">{children}</div>
    </div>
  );
}

export default function ContentEditor() {
  const content = useContent();
  const refresh = useContentRefresh();
  const loaded = useContentLoaded();
  const [f, setF] = useState(() => clone(content));
  const [saving, setSaving] = useState(false);
  const dirty = useRef(false);

  // Re-sincroniza el formulario con el contenido del servidor mientras el usuario no haya editado.
  useEffect(() => {
    if (!dirty.current) setF(clone(content));
  }, [content]);

  const set = (path, value) => {
    dirty.current = true;
    setF((prev) => {
      const next = clone(prev);
      let o = next;
      for (let i = 0; i < path.length - 1; i++) {
        if (o[path[i]] == null) o[path[i]] = {};
        o = o[path[i]];
      }
      o[path[path.length - 1]] = value;
      return next;
    });
  };
  const addAt = (path, item) => set(path, [...(pathGet(f, path) || []), item]);
  const rmAt = (path, idx) => set(path, (pathGet(f, path) || []).filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    const payload = { ...f, gallery: (f.gallery || []).filter((u) => u && u.trim()) };
    const doPut = async () => { await api.put("/content", payload); await refresh(); };
    try {
      await doPut();
      toast.success("Contenido guardado");
    } catch (e) {
      // Si fue fallo de red (p. ej. servidor despertando), reintenta una vez.
      if (!e.response) {
        try {
          await new Promise((r) => setTimeout(r, 1800));
          await doPut();
          toast.success("Contenido guardado");
          return;
        } catch {
          /* cae al mensaje de error */
        }
      }
      toast.error(
        e.response
          ? formatApiErrorDetail(e.response?.data?.detail)
          : "No se pudo conectar (el servidor puede estar despertando). Intenta de nuevo en unos segundos."
      );
    } finally {
      setSaving(false);
    }
  };

  const T = (path, placeholder) => (
    <input
      className="field-underline"
      placeholder={placeholder}
      value={pathGet(f, path) ?? ""}
      onChange={(e) => set(path, e.target.value)}
    />
  );
  const TA = (path, placeholder) => (
    <textarea
      className="field-underline resize-none"
      rows={2}
      placeholder={placeholder}
      value={pathGet(f, path) ?? ""}
      onChange={(e) => set(path, e.target.value)}
    />
  );

  if (!loaded) {
    return (
      <div className="py-24 text-center overline text-[color:var(--muted)]" data-testid="content-loading">
        Cargando contenido…
      </div>
    );
  }

  return (
    <div className="pb-28" data-testid="content-editor">
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="General">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><L>Novia</L>{T(["bride"], "Carolina")}</div>
            <div><L>Novio</L>{T(["groom"], "Roberto")}</div>
          </div>
          <div><L>Monograma</L>{T(["monogram"], "C & R")}</div>
          <div><L>Frase (tagline)</L>{T(["tagline"], "Te elijo hoy y para siempre.")}</div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><L>Fecha (para el countdown)</L>
              <input type="datetime-local" className="field-underline"
                value={(f.date || "").slice(0, 16)}
                onChange={(e) => set(["date"], e.target.value + ":00")} />
            </div>
            <div><L>Fecha (texto)</L>{T(["dateLabel"], "13 de Febrero, 2027")}</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><L>Fecha corta</L>{T(["dateShort"], "13.02.2027")}</div>
            <div><L>Hashtag</L>{T(["hashtag"], "#CarolinaYRoberto2027")}</div>
          </div>
        </Section>

        <Section title="Dónde y Cuándo">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><L>Hora — Ceremonia</L>{T(["venue", "ceremonyTime"], "5:30 pm")}</div>
            <div><L>Hora — Celebración</L>{T(["venue", "receptionTime"], "7:00 pm")}</div>
          </div>
          <div><L>Nombre del lugar</L>{T(["venue", "name"], "Hacienda…")}</div>
          <div><L>Subtítulo</L>{T(["venue", "line1"], "Ceremonia & Recepción")}</div>
          <div><L>Dirección</L>{TA(["venue", "address"], "Calle, colonia, ciudad")}</div>
          <div><L>Link de Google Maps</L>{T(["venue", "mapsUrl"], "https://maps.google.com/…")}</div>
          <div><L>Nota</L>{TA(["venue", "note"], "Estacionamiento…")}</div>
          <div>
            <L>Foto del lugar (link)</L>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 bg-secondary overflow-hidden shrink-0">
                {f.locationImage ? <img src={f.locationImage} alt="" className="w-full h-full object-cover grayscale" /> : null}
              </div>
              {T(["locationImage"], "https://…")}
            </div>
          </div>
        </Section>

        <Section title="Save the Date (YouTube)">
          <div><L>ID del video de YouTube</L>{T(["media", "saveTheDateYouTubeId"], "AbC123xyz")}</div>
          <p className="text-xs font-light text-[color:var(--muted)]">
            De la URL youtu.be/<b>AbC123xyz</b> o watch?v=<b>AbC123xyz</b>, pega solo esa parte.
          </p>
        </Section>

        <Section title="Hospedaje (Hotel)">
          <div><L>Nombre del hotel</L>{T(["hotel", "name"], "Hotel…")}</div>
          <div><L>Descripción</L>{TA(["hotel", "description"], "Reservamos una tarifa preferencial…")}</div>
          <div><L>Dirección</L>{T(["hotel", "address"], "Calle, colonia, ciudad")}</div>
          <div><L>Nota</L>{TA(["hotel", "note"], "Menciona el código \"BODA C&R\"…")}</div>
          <div><L>Link de reservación</L>{T(["hotel", "bookingUrl"], "https://…")}</div>
          <div>
            <L>Foto del hotel (link)</L>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 bg-secondary overflow-hidden shrink-0">
                {f.hotel?.image ? <img src={f.hotel.image} alt="" className="w-full h-full object-cover" /> : null}
              </div>
              {T(["hotel", "image"], "https://…")}
            </div>
          </div>
        </Section>

        <Section title="Código de Vestimenta">
          <div><L>Título</L>{T(["dressCode", "label"], "Etiqueta Rigurosa")}</div>
          <div><L>Subtítulo</L>{T(["dressCode", "sub"], "Black Tie")}</div>
          <div><L>Detalle</L>{TA(["dressCode", "detail"], "Ellos… Ellas…")}</div>
          <div>
            <L>Sugerencias de vestimenta (2 fotos)</L>
            <div className="grid grid-cols-2 gap-4">
              {[0, 1].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-secondary overflow-hidden shrink-0">
                    {f.dressCode?.images?.[i] ? (
                      <img src={f.dressCode.images[i]} alt="" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                  {T(["dressCode", "images", i], "https://…")}
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section title="Mesa de Regalos">
          <div><L>Intro</L>{TA(["gifts", "intro"], "Tu presencia es…")}</div>
          <div className="grid sm:grid-cols-3 gap-5">
            <div><L>Banco</L>{T(["gifts", "bank", "bankName"], "BBVA")}</div>
            <div><L>Titular</L>{T(["gifts", "bank", "holder"], "Carolina & Roberto")}</div>
            <div><L>CLABE (transferencias)</L>{T(["gifts", "bank", "clabe"], "012 …")}</div>
          </div>
          <div><L>Título de esta cuenta</L>{T(["gifts", "bank", "clabeLabel"], "CLABE · Transferencias")}</div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <L>Otras cuentas (banco + número)</L>
              <button onClick={() => addAt(["gifts", "accounts"], { bank: "", number: "" })}
                className="inline-flex items-center gap-1 text-xs hover:opacity-60" data-testid="add-account">
                <Plus size={14} /> Agregar
              </button>
            </div>
            <div className="space-y-4">
              {(f.gifts?.accounts || []).map((a, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.4fr_auto] gap-3 items-end">
                  <input className="field-underline" placeholder="Banco" value={a.bank}
                    onChange={(e) => set(["gifts", "accounts", i, "bank"], e.target.value)} />
                  <input className="field-underline" placeholder="Número / CLABE" value={a.number}
                    onChange={(e) => set(["gifts", "accounts", i, "number"], e.target.value)} />
                  <button onClick={() => rmAt(["gifts", "accounts"], i)} className="pb-3 text-[color:var(--muted)] hover:text-destructive">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <L>Tiendas</L>
              <button onClick={() => addAt(["gifts", "registries"], { store: "", url: "", code: "" })}
                className="inline-flex items-center gap-1 text-xs hover:opacity-60" data-testid="add-registry">
                <Plus size={14} /> Agregar
              </button>
            </div>
            <div className="space-y-4">
              {(f.gifts?.registries || []).map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
                  <input className="field-underline" placeholder="Tienda" value={r.store}
                    onChange={(e) => set(["gifts", "registries", i, "store"], e.target.value)} />
                  <input className="field-underline" placeholder="URL" value={r.url}
                    onChange={(e) => set(["gifts", "registries", i, "url"], e.target.value)} />
                  <input className="field-underline" placeholder="Código" value={r.code}
                    onChange={(e) => set(["gifts", "registries", i, "code"], e.target.value)} />
                  <button onClick={() => rmAt(["gifts", "registries"], i)} className="pb-3 text-[color:var(--muted)] hover:text-destructive">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section title="Itinerario">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!f.showItinerary}
              onChange={(e) => set(["showItinerary"], e.target.checked)} data-testid="toggle-itinerary" />
            <span className="text-sm font-light">Mostrar el itinerario en la página</span>
          </label>
          <div className="flex items-center justify-between">
            <L>Eventos</L>
            <button onClick={() => addAt(["itinerary"], { time: "", title: "", detail: "" })}
              className="inline-flex items-center gap-1 text-xs hover:opacity-60" data-testid="add-itinerary">
              <Plus size={14} /> Agregar
            </button>
          </div>
          <div className="space-y-4">
            {(f.itinerary || []).map((it, i) => (
              <div key={i} className="grid grid-cols-[90px_1fr_auto] gap-3 items-end">
                <input className="field-underline" placeholder="17:00" value={it.time}
                  onChange={(e) => set(["itinerary", i, "time"], e.target.value)} />
                <div className="space-y-2">
                  <input className="field-underline" placeholder="Título" value={it.title}
                    onChange={(e) => set(["itinerary", i, "title"], e.target.value)} />
                  <input className="field-underline" placeholder="Detalle" value={it.detail}
                    onChange={(e) => set(["itinerary", i, "detail"], e.target.value)} />
                </div>
                <button onClick={() => rmAt(["itinerary"], i)} className="pb-3 text-[color:var(--muted)] hover:text-destructive">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Notas Extra">
          <div className="flex items-center justify-between">
            <L>Notas</L>
            <button onClick={() => addAt(["notes"], { title: "", text: "" })}
              className="inline-flex items-center gap-1 text-xs hover:opacity-60" data-testid="add-note">
              <Plus size={14} /> Agregar
            </button>
          </div>
          <div className="space-y-4">
            {(f.notes || []).map((n, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto] gap-3 items-end">
                <div className="space-y-2">
                  <input className="field-underline" placeholder="Título" value={n.title}
                    onChange={(e) => set(["notes", i, "title"], e.target.value)} />
                  <input className="field-underline" placeholder="Texto" value={n.text}
                    onChange={(e) => set(["notes", i, "text"], e.target.value)} />
                </div>
                <button onClick={() => rmAt(["notes"], i)} className="pb-3 text-[color:var(--muted)] hover:text-destructive">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Galería (links de fotos)">
          <div className="flex items-center justify-between">
            <L>Imágenes</L>
            <button onClick={() => addAt(["gallery"], "")}
              className="inline-flex items-center gap-1 text-xs hover:opacity-60" data-testid="add-gallery">
              <Plus size={14} /> Agregar
            </button>
          </div>
          <div className="space-y-3">
            {(f.gallery || []).map((url, i) => (
              <div key={i} className="grid grid-cols-[56px_1fr_auto] gap-3 items-center">
                <div className="w-14 h-14 bg-secondary overflow-hidden">
                  {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <input className="field-underline" placeholder="https://…" value={url}
                  onChange={(e) => set(["gallery", i], e.target.value)} />
                <button onClick={() => rmAt(["gallery"], i)} className="text-[color:var(--muted)] hover:text-destructive">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 inset-x-0 bg-paper/90 backdrop-blur border-t border-black/10 z-20">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <p className="text-xs font-light text-[color:var(--muted)]">Los cambios se reflejan en la página al guardar.</p>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-ink text-white px-8 py-3.5 border border-ink hover:bg-white hover:text-ink transition-colors duration-300 overline disabled:opacity-50"
            data-testid="content-save">
            <Save size={15} /> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
